import React, { useState, useEffect } from 'react';
import { MDXProvider } from '@mdx-js/react';
import { useHelp } from '../hooks/useHelp';
import { GuideViewer } from './GuideViewer';
import { Screenshot } from './Screenshot';
import { Callout } from './Callout';
import styles from './HelpDrawer.module.css';

const MDX_COMPONENTS = { Screenshot, Callout };

// ─── HelpDrawer ──────────────────────────────────────────────────────────────

interface HelpDrawerProps {
  helpKey?: string;
  roleId?: number;
  isOpen: boolean;
  onClose: () => void;
}

type DrawerTab = 'screen' | 'guide';

export const HelpDrawer: React.FC<HelpDrawerProps> = ({
  helpKey,
  roleId,
  isOpen,
  onClose,
}) => {
  const hasScreen = Boolean(helpKey);
  const hasGuide = roleId != null;

  const [activeTab, setActiveTab] = useState<DrawerTab>(hasScreen ? 'screen' : 'guide');

  // איפוס tab כאשר helpKey משתנה (למשל ניווט בין מסכים)
  useEffect(() => {
    setActiveTab(hasScreen ? 'screen' : 'guide');
  }, [helpKey, hasScreen]);

  const { Content, frontmatter, loading, notFound } = useHelp(
    activeTab === 'screen' ? helpKey : null
  );

  const screenTitle = (frontmatter?.title as string) ?? helpKey ?? 'עזרה';

  if (!isOpen) return null;

  const showTabs = hasScreen && hasGuide;

  return (
    <>
      <div
        className={styles.backdrop}
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />

      <aside
        className={styles.drawer}
        role="complementary"
        aria-label="מדריך עזרה"
      >
        <header className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.helpIcon} aria-hidden="true">?</div>
            <h2 className={styles.headerTitle}>עזרה</h2>
          </div>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="סגור חלונית עזרה"
          >
            ✕
          </button>
        </header>

        {/* tabs — מוצגים רק אם שתי הרמות זמינות */}
        {showTabs && (
          <div className={styles.tabs} role="tablist" aria-label="רמת עזרה">
            <button
              role="tab"
              id="help-tab-screen"
              aria-controls="help-panel-screen"
              aria-selected={activeTab === 'screen'}
              className={`${styles.tab} ${activeTab === 'screen' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('screen')}
            >
              עזרה למסך
            </button>
            <button
              role="tab"
              id="help-tab-guide"
              aria-controls="help-panel-guide"
              aria-selected={activeTab === 'guide'}
              className={`${styles.tab} ${activeTab === 'guide' ? styles.tabActive : ''}`}
              onClick={() => setActiveTab('guide')}
            >
              מדריך תפקיד
            </button>
          </div>
        )}

        {/* panels */}
        <div className={styles.body}>
          {/* panel עזרה הקשרית — גולל בעצמו */}
          <div
            id="help-panel-screen"
            role={showTabs ? 'tabpanel' : undefined}
            aria-labelledby={showTabs ? 'help-tab-screen' : undefined}
            className={`${styles.panel} ${styles.panelScrollable}`}
            hidden={activeTab !== 'screen'}
          >
            {hasScreen && (
              <ScreenContent
                Content={Content}
                screenTitle={screenTitle}
                loading={loading}
                notFound={notFound}
              />
            )}
          </div>

          {/* panel מדריך תפקיד — GuideViewer מטפל בגלילה הפנימית שלו */}
          <div
            id="help-panel-guide"
            role={showTabs ? 'tabpanel' : undefined}
            aria-labelledby={showTabs ? 'help-tab-guide' : undefined}
            className={styles.panel}
            hidden={activeTab !== 'guide'}
          >
            {hasGuide && <GuideViewer roleId={roleId!} />}
          </div>
        </div>
      </aside>
    </>
  );
};

// ─── ScreenContent ────────────────────────────────────────────────────────────

interface ScreenContentProps {
  Content: React.ComponentType | null;
  screenTitle: string;
  loading: boolean;
  notFound: boolean;
}

const ScreenContent: React.FC<ScreenContentProps> = ({
  Content,
  screenTitle,
  loading,
  notFound,
}) => {
  if (loading) {
    return (
      <div className={styles.center}>
        <div className={styles.spinner} aria-label="טוען עזרה..." />
        <span>טוען עזרה...</span>
      </div>
    );
  }

  if (notFound || !Content) {
    return (
      <div className={styles.center}>
        <span aria-live="polite">אין עזרה זמינה למסך זה</span>
      </div>
    );
  }

  return (
    <div className={styles.screenHelp}>
      <h3 className={styles.screenTitle}>{screenTitle}</h3>
      <MDXProvider components={MDX_COMPONENTS}>
        <Content />
      </MDXProvider>
    </div>
  );
};

// ─── HelpButton ───────────────────────────────────────────────────────────────

interface HelpButtonProps {
  onClick: () => void;
  isOpen?: boolean;
}

export const HelpButton: React.FC<HelpButtonProps> = ({ onClick, isOpen = false }) => (
  <button
    className={styles.helpButton}
    onClick={onClick}
    aria-label={isOpen ? 'סגור מדריך עזרה' : 'פתח מדריך עזרה'}
    aria-expanded={isOpen}
    title="עזרה"
  >
    ?
  </button>
);
